import type { CollectionModelFieldFileTool, CollectionModelFieldImage } from '@ikegps/core-shared';
import { action, observable, makeObservable } from 'mobx';
import { Services } from '../services';
import { ImageCompression } from './image.compression';

const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/jpg'];
export const MAX_UPLOAD_FILE_SIZE_MB = 10;
export const MAX_UPLOAD_FILE_SIZE_BYTE = 1024 * 1024 * MAX_UPLOAD_FILE_SIZE_MB;

export type ImageValidationError = 'FILE_TOO_LARGE' | 'FILE_NOT_SUPPORTED';

export function validateImageFile(file: File): ImageValidationError | null {
	if (file.size > MAX_UPLOAD_FILE_SIZE_BYTE) {
		Services.Analytics.trackEvent(Services.Analytics.EventCategory.Upload, 'image:large', 'large', file.size);
		return 'FILE_TOO_LARGE';
	}

	const imageType = file.type.toLowerCase();
	if (!ALLOWED_IMAGE_TYPES.includes(imageType)) {
		Services.Analytics.trackEvent(Services.Analytics.EventCategory.Upload, 'image:type', imageType || 'unknown', 1);
		return 'FILE_NOT_SUPPORTED';
	}

	return null;
}

export class UniversalFileUploadReader<T> {
	type: string;
	name: string;
	size: number;
	field: T;
	fileReader: FileReader | null;

	@observable buffer: Uint8Array<ArrayBuffer> | null = null;
	@observable isFailed: boolean = false;
	@observable isLoading: boolean = true;

	constructor(
		public file: File,
		field: T
	) {
		makeObservable(this);

		this.name = file.name;
		this.type = file.type;
		this.size = file.size;
		this.field = field;

		this.fileReader = new FileReader();
		this.fileReader.onerror = this.onFileError;
		this.fileReader.onload = this.onFileLoad;
		this.fileReader.readAsArrayBuffer(file);
	}

	@action.bound onFileError() {
		this.fileReader?.abort();
		this.isFailed = true;
		this.isLoading = false;
	}

	@action.bound onFileLoad() {
		this.isFailed = false;
		this.isLoading = false;
		if (this.fileReader?.result && this.fileReader.result instanceof ArrayBuffer) {
			this.buffer = new Uint8Array(this.fileReader.result);
		}
		this.fileReader = null;
	}
}

export class FileUploadReader extends UniversalFileUploadReader<CollectionModelFieldFileTool> {}

export class ImageUploadReader extends UniversalFileUploadReader<CollectionModelFieldImage> {
	constructor(file: File, field: CollectionModelFieldImage) {
		super(file, field);

		makeObservable(this);

		// Override parent's onload to compress instead of using raw data
		if (this.fileReader) {
			this.fileReader.onload = () => this.compressAndLoad(file);
		}
	}

	@action.bound
	private async compressAndLoad(file: File) {
		try {
			// Compress the image using browser-image-compression library
			this.buffer = await ImageCompression.compressImage(file, {
				quality: 0.95,
			});
			this.isFailed = false;
			this.isLoading = false;
		} catch (error) {
			Services.Log.Logger.warn({ error }, 'Image compression failed, uploading original');
			// Fallback: read the original file with FileReader
			const fallbackReader = new FileReader();
			fallbackReader.onload = () => {
				if (fallbackReader.result && fallbackReader.result instanceof ArrayBuffer) {
					this.buffer = new Uint8Array(fallbackReader.result);
				}
				this.isFailed = false;
				this.isLoading = false;
			};
			fallbackReader.onerror = () => {
				this.isFailed = true;
				this.isLoading = false;
			};
			fallbackReader.readAsArrayBuffer(file);
		}
	}
}
